import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { onboardingKeys } from './queryKeys';
import { fetchSnapshot, isSessionNotFound } from './useSessionSnapshot';

/** Re-attaches the BFF session (a snapshot refetch that re-PUTs on 404) and settles when it is back. */
export type RecoverSession = () => Promise<void>;

/**
 * The shared answer to "the BFF forgot this session" (404 `session_not_found`), whichever call noticed it: a
 * snapshot refetch that re-PUTs the same id and url. It goes through the query cache's `fetchQuery`, so the
 * calls that notice the same loss at once (a mutation, two queries, the poll itself) share one request.
 */
export function useSessionRecovery(sessionId: string, url: string | null): RecoverSession {
  const client = useQueryClient();
  const urlRef = useRef(url);
  urlRef.current = url;
  return useCallback(async () => {
    await client.fetchQuery({
      queryKey: onboardingKeys.snapshot(sessionId),
      queryFn: () => fetchSnapshot(sessionId, urlRef.current),
      staleTime: 0,
    });
  }, [client, sessionId]);
}

/**
 * Runs a BFF call; if it fails with `session_not_found`, recovers the session and runs it once more. Any other
 * failure, a failed recovery, or a second 404 reaches the caller (the first error when recovery itself failed).
 */
export async function withRecovery<T>(call: () => Promise<T>, recover?: RecoverSession): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (!recover || !isSessionNotFound(error)) throw error;
    try {
      await recover();
    } catch {
      throw error;
    }
    return call();
  }
}
